import type {
  AlertDrawerData,
  AlertFilters,
  AlertSettings,
  AlertSettingsUpdate,
  AlertsPage,
  DashboardAnalytics,
  DashboardFilters,
  EventFilters,
  Features,
  FilterMetadata,
  IpEvent,
  PaginatedResponse,
  QueriedIpsPage,
  PortalOverview,
  PortalSourcesPage,
  SecurityOverviewData,
  CredentialHealthData,
  SnapshotInfo,
  SourcesFilters,
  VulnerabilityDetail,
  VulnerabilityWatch,
} from "@/types";
import type { AuthConfig } from "@/auth/authConfig";
import type { UserInfo } from "@/auth/AuthContext";
import { buildSourcesFilterParams, SOURCES_PAGE_SIZE } from "@/utils/sourcesFilters.ts";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

type TokenGetter = () => Promise<string | null>;

// Token getter — set by main.tsx after MSAL init
let _getToken: TokenGetter | null = null;
let _tokenPromise: Promise<string | null> | null = null;
// Company override — set by AuthContext when admin switches company
let _companyOverride: string | null = null;

export function setTokenGetter(fn: TokenGetter | null): void {
  if (_getToken !== null && fn !== null) {
    console.warn("Token getter already set — overwriting previous setter");
  }
  _getToken = fn;
  _tokenPromise = null;
}

export function setCompanyOverride(companyId: string | null): void {
  _companyOverride = companyId;
}

export function clearAuthState(): void {
  _companyOverride = null;
}

function clearInFlightToken(tokenPromise: Promise<string | null>): void {
  if (_tokenPromise === tokenPromise) {
    _tokenPromise = null;
  }
}

async function getToken(): Promise<string | null> {
  const tokenGetter = _getToken;
  if (!tokenGetter) return null;

  if (!_tokenPromise) {
    const tokenPromise = Promise.resolve().then(tokenGetter);
    _tokenPromise = tokenPromise;
    void tokenPromise.then(
      () => clearInFlightToken(tokenPromise),
      () => clearInFlightToken(tokenPromise),
    );
  }

  return _tokenPromise;
}

class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };

  // Attach Bearer token if available
  if (_getToken) {
    const token = await getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  // Attach company override header for admin company switching
  if (_companyOverride) {
    headers["X-Company-Override"] = _companyOverride;
  }

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      body || `Request failed: ${response.statusText}`,
      response.status,
    );
  }

  return response.json() as Promise<T>;
}

/** Unauthenticated request (for auth config bootstrap). */
async function publicRequest<T>(path: string): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new ApiError(`Request failed: ${response.statusText}`, response.status);
  }
  return response.json() as Promise<T>;
}

async function publicAction(path: string): Promise<void> {
  const url = `${BASE_URL}${path}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new ApiError(`Request failed: ${response.statusText}`, response.status);
  }
}

function toQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        searchParams.append(key, String(v));
      }
    } else {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export const api = {
  dashboard: {
    async getAnalytics(filters?: DashboardFilters): Promise<DashboardAnalytics> {
      const qs = filters ? toQueryString(filters as Record<string, unknown>) : "";
      return request(`/api/v1/customer/analytics${qs}`);
    },

    async getEvents(
      filters?: EventFilters,
      limit = 50,
      offset = 0,
    ): Promise<PaginatedResponse<IpEvent>> {
      const params = { ...filters, limit, offset } as Record<string, unknown>;
      return request(`/api/v1/customer/events${toQueryString(params)}`);
    },

    async getFilterMetadata(): Promise<FilterMetadata> {
      return request("/api/v1/customer/filter-metadata");
    },
  },

  securityOverview: {
    async getData(days?: number): Promise<SecurityOverviewData> {
      const qs = days ? `?days=${days}` : "";
      return request(`/api/v1/customer/security-overview${qs}`);
    },

    async getQueriedIps(neverIngested: boolean, continuation?: string | null): Promise<QueriedIpsPage> {
      const qs = toQueryString({ neverIngested, continuation, limit: 100 });
      return request(`/api/v1/customer/queried-ips${qs}`);
    },
  },

  credentialHealth: {
    async getData(): Promise<CredentialHealthData> {
      return request("/api/v1/customer/credential-health");
    },
  },

  features: {
    async get(): Promise<Features> {
      return request("/api/v1/customer/features");
    },
  },

  vulnerabilityWatch: {
    async get(): Promise<VulnerabilityWatch> {
      return request("/api/v1/customer/vulnerability-watch");
    },
    async getOne(cve: string): Promise<VulnerabilityDetail> {
      return request(`/api/v1/customer/vulnerability-watch/${encodeURIComponent(cve)}`);
    },
    async getPortalOverview(): Promise<PortalOverview> {
      return request("/api/v1/customer/portal-overview");
    },
    async getPortalSources(filters: SourcesFilters, page: number): Promise<PortalSourcesPage> {
      const params = buildSourcesFilterParams(filters);
      params.set("limit", String(SOURCES_PAGE_SIZE));
      params.set("offset", String(page * SOURCES_PAGE_SIZE));
      return request(`/api/v1/customer/portal-sources?${params.toString()}`);
    },
  },
  alerts: {
    async list(filters: AlertFilters, continuation?: string | null): Promise<AlertsPage> {
      const qs = toQueryString({ rule: filters.rule, period_days: filters.period_days, continuation });
      return request(`/api/v1/customer/alerts${qs}`);
    },

    async get(alertId: string): Promise<AlertDrawerData> {
      return request(`/api/v1/customer/alerts/${encodeURIComponent(alertId)}`);
    },
  },

  alertSettings: {
    async get(): Promise<AlertSettings> {
      return request("/api/v1/customer/alert-settings");
    },

    async put(update: AlertSettingsUpdate): Promise<AlertSettings> {
      return request("/api/v1/customer/alert-settings", { method: "PUT", body: JSON.stringify(update) });
    },
  },

  health: {
    check(): Promise<{ status: string }> {
      return request("/api/v1/health");
    },
  },

  snapshot: {
    async getInfo(): Promise<SnapshotInfo> {
      return request("/api/v1/customer/snapshot-info");
    },
  },

  auth: {
    async getConfig(): Promise<AuthConfig> {
      return publicRequest("/api/v1/auth/config");
    },
    async getMe(): Promise<UserInfo> {
      return request("/api/v1/auth/me");
    },
    async clearBackendSession(): Promise<void> {
      await publicAction("/api/v1/auth/logout");
    },
    async getCompanies(): Promise<{ companies: string[] }> {
      return request("/api/v1/companies");
    },
  },
};

export { ApiError };
